/**
 * services/api.js — everything the website reads from, or sends to, the
 * Makua backend.
 *
 * The address comes from REACT_APP_API_URL at build time
 * (e.g. https://api.makuaretreats.com/api). In development it falls back
 * to the local backend.
 */
const BASE = (process.env.REACT_APP_API_URL || "http://localhost:5055/api").replace(/\/$/, "");

/** Where the API lives, without the trailing /api — uploads are served there. */
const API_ORIGIN = BASE.replace(/\/api\/?$/, "");
/**
 * A stored image path turned into something a browser can actually load.
 *
 * Uploaded pictures are written by the backend and served from its own
 * host (/uploads/...). The website and the admin panel are on different
 * hostnames, so a bare "/uploads/media/x.png" would resolve against THEM
 * and 404. Files that ship with the website stay relative, because those
 * really are served from the site itself.
 */
export const mediaUrl = (p) => {
  if (!p) return "";
  if (/^(https?:)?\/\//i.test(p) || p.startsWith("data:")) return p;
  if (p.startsWith("/uploads/")) return `${API_ORIGIN}${p}`;
  return p;
};

/**
 * The sized AVIF and WebP copies the backend makes of every uploaded
 * picture (spec 09), as srcset strings — or null for a picture that has
 * none (anything that isn't "/uploads/…/<id>-o.jpg|png", e.g. files that
 * ship with the site). See components/utils/Picture.js.
 */
const VARIANT_WIDTHS = [400, 800, 1200, 1600];
export const mediaSet = (p) => {
  const m = String(p || "").replace(/^(?!\/)/, "/").match(/^(\/uploads\/[a-z-]+\/[^/]+-o)\.(jpg|png)$/);
  if (!m) return null;
  const base = mediaUrl(m[1]);
  const set = (fmt) => VARIANT_WIDTHS.map((w) => `${base}-w${w}.${fmt} ${w}w`).join(", ");
  return { avif: set("avif"), webp: set("webp") };
};

async function request(path, options = {}) {
  const res = await fetch(`${BASE}${path}`, {
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
    ...options,
  });

  let body = null;
  try { body = await res.json(); } catch (_) { /* empty or not JSON */ }

  if (!res.ok || body?.success === false) {
    const err = new Error(body?.message || "Something went wrong. Please try again.");
    err.status = res.status;
    throw err;
  }
  return body?.data ?? {};
}

/* ── What's on sale ─────────────────────────────────────── */
export const retreatsAPI = {
  list: () => request("/public/retreats").then((d) => d.retreats || []),
  get: (slug) => request(`/public/retreats/${encodeURIComponent(slug)}`).then((d) => d.retreat),
};

/* ── Retreat types, dates and booking (the 5-step flow) ─── */
export const typesAPI = {
  /** The evergreen cards, each with its upcoming dates. */
  list: () => request("/public/retreat-types").then((d) => d.types || []),
  get: (slug) => request(`/public/retreat-types/${encodeURIComponent(slug)}`).then((d) => d.type),
};

export const datesAPI = {
  /** Every upcoming published date, chronological, plus the types to filter by. */
  list: () => request("/public/dates"),
};

/* ── Partner referral links: makuaretreats.com/?ref=CODE ──────────────
   A visitor who arrives through a partner's link keeps that partner for
   30 days, so a booking made later on is still credited to them. */
const REF_KEY = "makua_ref";
const REF_DAYS = 30;
export const referral = {
  /** The live code, or null. */
  get: () => {
    try {
      const r = JSON.parse(localStorage.getItem(REF_KEY) || "null");
      if (r && Date.now() - r.at < REF_DAYS * 864e5) return r.code;
      localStorage.removeItem(REF_KEY);
    } catch (_) { /* storage blocked: no credit, nothing breaks */ }
    return null;
  },
  /** Remember a code from the address, once the backend says it's real. */
  capture: async (code) => {
    if (!/^[A-Za-z0-9-]{3,40}$/.test(String(code || ""))) return;
    try {
      const d = await request(`/public/referral/${encodeURIComponent(code)}`);
      if (d.valid) localStorage.setItem(REF_KEY, JSON.stringify({ code: d.code, at: Date.now() }));
    } catch (_) { /* unknown code or offline: ignore */ }
  },
};

export const reserveAPI = {
  /** Rooms, add-ons, diets and payment options for one date, for a party and extra nights. */
  options: (key, { guests = 1, before = 0, after = 0 } = {}) =>
    request(`/public/booking/${encodeURIComponent(key)}?guests=${guests}&before=${before}&after=${after}`),
  quote: (key, payload) =>
    request(`/public/booking/${encodeURIComponent(key)}/quote`, { method: "POST", body: JSON.stringify(payload) }),
  waitlist: (payload) => request("/public/waitlist", { method: "POST", body: JSON.stringify(payload) }),
};

/* ── The site's own chrome: name, contact, socials, legal ─ */
export const siteAPI = {
  get: () => request("/public/site"),
};

/* ── Workshops and one-off events ───────────────────────── */
export const workshopsAPI = {
  list: ({ from, to } = {}) => {
    const q = new URLSearchParams();
    if (from) q.set("from", from);
    if (to) q.set("to", to);
    const qs = q.toString();
    return request(`/public/workshops${qs ? `?${qs}` : ""}`).then((d) => d.workshops || []);
  },
  signup: (id, payload) =>
    request(`/public/workshops/${id}/signup`, { method: "POST", body: JSON.stringify(payload) }),
};

/* ── The shop ───────────────────────────────────────────── */
export const shopAPI = {
  list: () => request("/public/products").then((d) => d.products || []),
  get: (slug) => request(`/public/products/${encodeURIComponent(slug)}`),
  info: () => request("/public/shop-info"),
  order: (payload) => request("/public/orders", { method: "POST", body: JSON.stringify(payload) }),
};

/* ── Forms ──────────────────────────────────────────────── */
export const formsAPI = {
  enquiry: (payload) => request("/public/enquiries", { method: "POST", body: JSON.stringify(payload) }),
  subscribe: (email, source) =>
    request("/public/subscribe", { method: "POST", body: JSON.stringify({ email, source }) }),
};

/* ── Formatting used across the site ────────────────────── */
export const money = (n) =>
  Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

/**
 * Dates arrive as wall-clock strings with no timezone on them —
 * "2026-11-06" or "2026-10-02 19:00:00" — because a retreat that starts
 * on the 6th starts on the 6th wherever you're reading this from.
 *
 * `new Date("2026-11-06")` would read that as UTC midnight and show
 * 5 November to anyone in the Americas, so the pieces are handed to the
 * Date constructor separately, which builds it in local time.
 */
export const parseDate = (v) => {
  if (!v) return null;
  if (v instanceof Date) return v;
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/);
  if (!m) return new Date(v);
  const [, y, mo, d, hh = 0, mi = 0, ss = 0] = m;
  return new Date(+y, +mo - 1, +d, +hh, +mi, +ss);
};

export const shortDate = (d) =>
  d ? parseDate(d).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";

export const longDate = (d) =>
  d ? parseDate(d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "";

/** "Nov 6 – 13, 2026" */
export const dateRange = (a, b) => {
  if (!a) return "";
  const s = parseDate(a), e = b ? parseDate(b) : null;
  const year = (e || s).getFullYear();
  if (!e) return `${shortDate(a)}, ${year}`;
  const sameMonth = s.getMonth() === e.getMonth();
  return `${shortDate(a)} – ${sameMonth ? e.getDate() : shortDate(b)}, ${year}`;
};

export const timeOfDay = (d) =>
  d ? parseDate(d).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "";

/** "7 nights", "1 night" */
export const nightsLabel = (n) => `${n} night${Number(n) === 1 ? "" : "s"}`;

/** "8 days / 7 nights" — how the retreat world talks about length. */
export const daysNights = (n) => `${Number(n) + 1} days / ${nightsLabel(n)}`;

/** "November 2026" */
export const monthLabel = (d) =>
  d ? parseDate(d).toLocaleDateString("en-US", { month: "long", year: "numeric" }) : "";

/** Price with cents only when there are cents. */
export const cash = (n) => {
  const v = Number(n || 0);
  return v.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: Number.isInteger(v) ? 0 : 2, maximumFractionDigits: 2 });
};
